import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Container, Text } from '@pixi/react';
import gsap from 'gsap';

import { dotFormatter, isMobile, roundNumber } from '../../../../../../helpers';
import { JackPotNumbersItemSymbol } from '../jackPotNumberItemSymbol';
import { useStore } from '../../../../../../hooks';
import { Spine } from '../../../../../components';

let timeLine: any | gsap.core.Timeline = null;
export const JackPotSceneNumberItemContent: React.FC<any> = observer(({
  x,
  y,
  id,
  type,
  image,
  winIcon,
  containerName,
}) => {
  const amountRef = useRef(null);
  const amountBackgroundRef: any = useRef(null);
  const { jackpotStore, gameStore } = useStore();
  const { isWinMoveAndScaleSymbolComplete, jackpotSceneAmounts } = jackpotStore;
  const { precision } = gameStore;

  const [currentTimeJackPotWinAmount, setCurrentTimeJackPotWinAmount] = useState<string>('0');
  const isWinIcon: boolean = id === winIcon?.jackpotCard;

  const amount = useMemo(() => {
    return jackpotSceneAmounts[type] ? `${roundNumber(jackpotSceneAmounts[type], precision)}` : '';
  }, [jackpotSceneAmounts, type, precision]);

  useEffect(() => {
    setCurrentTimeJackPotWinAmount(amount);
  }, [amount]);

  useEffect(() => {
    const jackpotAmountBoardSpine: Spine = new Spine({ key: 'jackpotAmountBoard' });
    jackpotAmountBoardSpine.play('shine', true);
    amountBackgroundRef.current.addChild(jackpotAmountBoardSpine);
    if (winIcon?.jackpotCard === id && isWinMoveAndScaleSymbolComplete) {      
      jackpotAmountBoardSpine.play('win', true);
      jackpotAmountBoardSpine.addListener(
        'complete',
        () => {
          jackpotStore.setIsShowJackPotWinPopup(true);
        },
        null,
        'win',
      );
    };
  }, [id, isWinMoveAndScaleSymbolComplete, winIcon]);

  useEffect(() => {
    if (!isWinIcon) return;
    timeLine && timeLine.kill();
    timeLine = gsap.timeline();
    const isInteger: boolean = Number(amount) === Math.floor(Number(amount));

    timeLine.to(
      { value: currentTimeJackPotWinAmount },
      {
        value: amount,
        duration: 5,
        snap: { textContent: isInteger ? 1 : 1 / 10 ** 2 },
        stagger: {
          each: 0.1,
          onUpdate: function () {
            const newAmount = +roundNumber(
              +(this as any).targets()[0].value,
              2
            );
            setCurrentTimeJackPotWinAmount(String(newAmount));
          },
          onComplete: function () {
            timeLine.kill();
          },
        },
      }
    );
  }, [amount, isWinIcon]);

  const detectFontSize = (newAmount: string) => {
    return isMobile()
      ? `${newAmount}`.length > 14
        ? 16
        : 18
      : `${newAmount}`.length > 14
        ? 22
        : 24;
  };

  const displayAmount = dotFormatter(+(currentTimeJackPotWinAmount || amount), 2);

  return (
    <Container x={x} y={y} {...({ name: { containerName } } as any)}>
      <Container
        y={isMobile() ? 25 : 50}
        ref={amountBackgroundRef}
        {...({ name: 'JackPotAmountBoardContainer' } as any)}
      />
      <JackPotNumbersItemSymbol image={image} containerName='JackPotWinSymbolContainer' />
      <Text
        anchor={0.5}
        ref={amountRef}
        y={isMobile() ? 32 : 55}
        {...({ interactive: false } as any)}
        text={displayAmount}
        style={{
          fill: '#FFFFFF',
          fontWeight: '600',
          fontFamily: 'poppinsMedium, Pascal-Font, Poppins, Arial',
          fontSize: detectFontSize(displayAmount),
        }}
      />
    </Container>
  );
});
